import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetShell } from '../SheetShell';
import { ListStateShell } from '../ListStateShell';
import { useListFetch } from '../useListFetch';

/**
 * BulkRentEditForm — I5/§6/AC5.6, the narrow "bulk-edit" reading cmdr ratified: a bulk RENT edit
 * across a Property's active Leases (the real recurring trigger, an annual increase across a
 * building), not a generic "edit anything in bulk" tool.
 *
 * Reads GET /api/properties/:id/active-leases (its own scoped read, §6 — never reused from the units
 * grid, which has no lease/rent data). One submit POSTs every row's current rent input to POST
 * /api/properties/:id/leases/bulk-rent → PropertyService.bulkUpdateRent, ONE transaction server-side
 * (AC5.6: all rows land, or none do) — this form never discovers a partial apply, because the server
 * makes one impossible.
 */
interface ActiveLeaseRow { leaseId: string; unitId: string; unitLabel: string; tenantName: string | null; rent: number }

export const BulkRentEditForm: React.FC<{
  propertyId: string;
  propertyName: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ propertyId, propertyName, onClose, onSaved }) => {
  const { t } = useTranslation('common');
  const { status, data, retry } = useListFetch<ActiveLeaseRow[]>(`/api/properties/${propertyId}/active-leases`);
  const rows = data ?? [];
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the drafts once the real rows arrive — each row's own current rent, never a blank/guessed
  // default, so an untouched row still submits its real unchanged value.
  useEffect(() => {
    if (rows.length === 0) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of rows) if (next[r.leaseId] === undefined) next[r.leaseId] = String(r.rent);
      return next;
    });
  }, [rows]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || rows.length === 0) return;
    const updates = rows.map((r) => ({ leaseId: r.leaseId, rent: Number(drafts[r.leaseId]) }));
    if (updates.some((u) => !Number.isFinite(u.rent))) { setError(t('portfolio.setup.bulkRent.error')); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/properties/${propertyId}/leases/bulk-rent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) { setError(t('portfolio.setup.bulkRent.error')); return; }
      onSaved();
    } catch {
      setError(t('portfolio.setup.bulkRent.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SheetShell shape="center" onClose={onClose} testId="bulk-rent-form">
      <form onSubmit={submit} className="p-4 flex flex-col gap-3" data-testid="bulk-rent-edit-form">
        <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide">
          {t('portfolio.setup.bulkRent.title', { property: propertyName })}
        </div>
        <ListStateShell status={status} testIdBase="bulk-rent" onRetry={retry}>
          {rows.length === 0 ? (
            <div className="py-4 text-sm text-text-muted" data-testid="bulk-rent-empty">{t('portfolio.setup.bulkRent.empty')}</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto" data-testid="bulk-rent-rows">
              {rows.map((r) => (
                <div key={r.leaseId} className="flex items-center gap-2" data-testid="bulk-rent-row">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-text-strong truncate">{r.unitLabel}</div>
                    <div className="text-xs text-text-muted truncate">{r.tenantName ?? '—'}</div>
                  </div>
                  <input
                    type="number"
                    inputMode="decimal"
                    data-testid="bulk-rent-input"
                    value={drafts[r.leaseId] ?? String(r.rent)}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.leaseId]: e.target.value }))}
                    className="h-10 w-24 px-2 rounded-lg border border-border-neutral text-sm text-end"
                    dir="ltr"
                  />
                </div>
              ))}
            </div>
          )}
        </ListStateShell>
        {error && <div className="text-xs text-opt-rose" data-testid="bulk-rent-error">{error}</div>}
        <div className="flex items-center gap-2 pt-1">
          <button type="submit" disabled={submitting || rows.length === 0} data-testid="bulk-rent-submit"
            className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
            {t('portfolio.setup.bulkRent.submit')}
          </button>
          <button type="button" onClick={onClose} data-testid="bulk-rent-cancel"
            className="min-h-[44px] px-4 rounded-lg text-sm font-semibold text-text-muted">
            {t('portfolio.setup.bulkRent.cancel')}
          </button>
        </div>
      </form>
    </SheetShell>
  );
};
